import * as React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";

import type {
    JourneyFeedbackCreatePayload,
    JourneyFeedbackMetadata,
} from "@volley/data";

import logger from "../../log";
import lightTheme from "../../theme";
import { fetchApi } from "../../util";
import CloseableDialogTitle from "../common/CloseableDialogTitle";
import FiveStarRating from "../common/FiveStarRating";

interface JourneyFeedbackDialogProps {
    open: boolean;
    onClose: () => void;
    journeyType: "assessment" | "class";
    metadata: JourneyFeedbackMetadata;
}

export default function JourneyFeedbackDialog({
    open,
    onClose,
    journeyType,
    metadata,
}: JourneyFeedbackDialogProps): React.JSX.Element {
    const [rating, setRating] = React.useState<number | null>(null);
    const [comment, setComment] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [apiError, setApiError] = React.useState("");

    const handleSubmit = async () => {
        if (!rating) return;

        setSubmitting(true);
        setApiError("");

        const payload: JourneyFeedbackCreatePayload = {
            rating,
            comment: comment || null,
            type: journeyType,
            metadata,
        };

        try {
            await fetchApi("/api/feedback/journey", "POST", payload);
            setRating(null);
            setComment("");
            onClose();
        } catch (e) {
            logger.error("Error submitting journey feedback", e as Error);
            setApiError("Failed to submit feedback. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const label = journeyType === "assessment" ? "assessment" : "classes";

    return (
        <ThemeProvider theme={lightTheme}>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <CloseableDialogTitle onClose={onClose}>
                    How was your {label} experience?
                </CloseableDialogTitle>
                <DialogContent>
                    <Box display="flex" justifyContent="center" component="div">
                        <FiveStarRating
                            name="journey-feedback-rating"
                            value={rating}
                            onChange={(_, newValue) => setRating(newValue)}
                            sx={{ fontSize: "3rem", mb: 4 }}
                        />
                    </Box>
                    <TextField
                        id="journey-feedback-comment"
                        label="Additional Comments"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Box display="flex" flexDirection="column" width="100%">
                        <Button
                            onClick={handleSubmit}
                            color="primary"
                            disabled={rating === null}
                            loading={submitting}
                            fullWidth
                        >
                            Submit
                        </Button>
                        {apiError && (
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ mt: 1, textAlign: "center" }}
                                role="alert"
                            >
                                {apiError}
                            </Typography>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}
